"use client";

import * as React from "react";
import { Calendar, User, Tag, ArrowLeft, Share2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl animate-pulse space-y-8 py-12">
        <div className="h-8 w-32 rounded-md bg-muted"></div>
        <div className="h-16 w-3/4 rounded-md bg-muted"></div>
        <div className="flex gap-4">
          <div className="h-6 w-24 rounded-md bg-muted"></div>
          <div className="h-6 w-24 rounded-md bg-muted"></div>
        </div>
        <div className="h-[400px] w-full rounded-xl bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded-md bg-muted"></div>
          <div className="h-4 w-full rounded-md bg-muted"></div>
          <div className="h-4 w-5/6 rounded-md bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl py-24 text-center">
        <h2 className="font-default text-2xl font-bold text-destructive">Error loading article</h2>
        <p className="mt-2 text-muted-foreground">{error}</p>
        <Link to="/ResidencesPage" className="mt-6 inline-block">
          <Button variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">Return to Listings</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl py-24 text-center">
        <h2 className="font-default text-2xl font-bold text-foreground">Article not found</h2>
        <p className="mt-2 text-muted-foreground">The rental guide or news article you are looking for does not exist.</p>
        <Link to="/ResidencesPage" className="mt-6 inline-block">
          <Button variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">Return to Listings</Button>
        </Link>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-4xl">
      {}
      <header className="mb-10 space-y-6 text-center">
        <div className="flex items-center justify-center gap-2 text-sm font-semibold text-primary">
          {post.categories && post.categories.length > 0 && (
            <div className="flex items-center gap-2">
              {post.categories.map((cat, i) => (
                <span key={cat.id} data-index={i} className="uppercase tracking-widest">
                  {cat.name}
                </span>
              ))}
            </div>
          )}
        </div>

        <h1 className="font-default text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4 text-primary" />
              <span className="font-medium">{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4 text-primary" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-xl">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-[21/9] w-full object-cover transition-transform duration-700 hover:scale-105"
          />
        </div>
      )}

      {}
      <div className="prose prose-lg prose-neutral dark:prose-invert mx-auto max-w-3xl font-default text-foreground leading-relaxed">
        {post.content ? (
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <p className="text-muted-foreground italic">No content available for this rental guide.</p>
        )}
      </div>

      {}
      <div className="mx-auto mt-12 flex max-w-3xl flex-col items-center justify-between gap-6 sm:flex-row">
        <div className="flex flex-wrap items-center gap-2">
          <Tag className="size-4 text-primary" />
          {post.tags && post.tags.length > 0 ? (
            post.tags.map((tag, i) => (
              <span
                key={tag.id}
                data-index={i}
                className="rounded-md bg-secondary px-3 py-1 text-xs font-semibold text-primary"
              >
                {tag.name}
              </span>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">Urban Living</span>
          )}
        </div>
        <Button variant="outline" size="sm" className="gap-2 rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          <Share2 className="size-4" />
          Share Guide
        </Button>
      </div>

      <Separator className="mx-auto my-12 max-w-3xl bg-border" />

      {}
      {post.author && (
        <div className="mx-auto max-w-3xl rounded-lg bg-accent/50 p-8 sm:flex sm:items-center sm:gap-6 shadow-sm">
          <Avatar className="mb-4 size-20 border-2 border-primary shadow-md sm:mb-0">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="bg-primary text-primary-foreground text-xl">{post.author.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <h3 className="font-default text-xl font-bold text-foreground">
              Written by {post.author.name}
            </h3>
            <p className="mt-2 text-muted-foreground font-default">
              Urban Living Rentals specialist providing expert advice on finding your perfect city apartment and navigating the rental market.
            </p>
            {post.author.link && (
              <Link to={post.author.link} className="mt-4 inline-block text-sm font-bold text-primary hover:underline">
                More from {post.author.name} →
              </Link>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsGrid() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-[300px] animate-pulse rounded-lg bg-muted"></div>
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => (
        <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-none bg-transparent transition-all hover:bg-accent/30 rounded-lg">
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden rounded-lg shadow-md">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="px-0 pb-4 pt-6">
            <div className="mb-2 flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-wider">
              {post.categories?.[0]?.name || "Rental Tips"}
            </div>
            <CardTitle className="font-default text-xl font-bold leading-tight text-foreground group-hover:text-primary transition-colors">
              <Link to={post.link || "#"} className="before:absolute before:inset-0">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="mt-auto px-0 pb-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="size-4 text-primary" />
              <time dateTime={post.date}>
                {post.date ? new Date(post.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Recent"}
              </time>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}



export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== "undefined" && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", per_page: 1 };
    
  const mainPostQuery = new WP_Query(currentWPQueryParams);
  
  
  const relatedPostsQuery = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <section data-section-id="240659" id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div className="mb-8">
          <Link to="/ResidencesPage">
            <Button variant="ghost" className="text-primary font-semibold hover:bg-accent hover:text-primary rounded-md">
              <ArrowLeft className="mr-2 size-4" />
              Back to Residences
            </Button>
          </Link>
        </div>

        {}
        <PostsProvider wp_query={mainPostQuery}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mt-24 border-t border-border pt-16">
          <div className="mb-10 flex items-center justify-between">
            <h2 className="font-default text-3xl font-bold text-foreground">Explore More Guides</h2>
            <Link to="/ResidencesPage">
              <Button variant="outline" className="hidden sm:flex rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">View All Articles</Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsGrid />
          </PostsProvider>
          
          <div className="mt-8 flex justify-center sm:hidden">
            <Link to="/ResidencesPage" className="w-full">
              <Button variant="outline" className="w-full rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">View All Articles</Button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}